import { getHelpSections, parseHelpRoute, readAppLocale, sectionFromPathPart } from './data/helpData';
import { accountSectionFromPath, accountPathForSection } from './data/accountData';

export function isAuthRoute(page) {
  return page === 'login' || page === 'register' || page === 'restorePassword' || page === 'oauthCallback';
}

const APP_LOCALES = new Set(['tr', 'en']);

function stripLocalePath(pathname) {
  const raw = String(pathname || '/');
  const m = raw.match(/^\/(tr|en)(?=\/|$)/i);
  if (!m) return { locale: null, pathname: raw || '/' };
  const locale = m[1].toLowerCase();
  if (!APP_LOCALES.has(locale)) return { locale: null, pathname: raw || '/' };
  const rest = raw.slice(m[0].length) || '/';
  return { locale, pathname: rest.startsWith('/') ? rest : `/${rest}` };
}

function currentLocalePrefix() {
  try {
    return stripLocalePath(window.location.pathname).locale;
  } catch {
    return null;
  }
}

function withLocalePath(pathname, locale = currentLocalePrefix()) {
  const path = pathname || '/';
  if (!locale || !APP_LOCALES.has(locale)) return path;
  if (path === '/') return `/${locale}/`;
  return `/${locale}${path.startsWith('/') ? path : `/${path}`}`;
}


function pageFromPath(pathname) {
  if (pathname === '/vip' || pathname.startsWith('/vip/')) return 'vip';
  if (pathname.startsWith('/aff/')) return 'affiliateGate';
  if (pathname.startsWith('/authProcess/login')) return 'login';
  if (pathname.startsWith('/authProcess/registration')) return 'register';
  if (pathname.startsWith('/auth/sign-up') || pathname.startsWith('/auth/signup')) return 'register';
  if (pathname.startsWith('/authProcess/restorePassword')) return 'restorePassword';
  if (pathname.startsWith('/auth/oauth/callback')) return 'oauthCallback';
  if (pathname.startsWith('/promo-vip')) return 'vip';
  if (pathname.startsWith('/account')) return 'account';
  if (pathname.startsWith('/promos')) return 'promos';
  if (pathname.startsWith('/help-center')) return 'help';
  if (pathname === '/basinda-biz' || pathname.startsWith('/basinda-biz/') || pathname.startsWith('/info/basinda-biz')) return 'basindaBiz';
  if (pathname.startsWith('/pages/')) return 'cms';
  if (pathname.startsWith('/sport')) return 'sport';
  if (pathname === '/vurgun-tv' || pathname.startsWith('/vurgun-tv/')) return 'vurgunTv';
  if (pathname.startsWith('/casino')) return 'casino';
  if (pathname === '/' || pathname === '') return 'home';
  return 'notFound';
}

function cmsSlugFromPath(pathname) {
  if (!pathname.startsWith('/pages/')) return null;
  const slug = pathname.replace(/^\/pages\/?/, '').split('/').filter(Boolean)[0] || null;
  return slug;
}

export function parseCasinoGameRoute(gamePart) {
  if (!gamePart) return { gameId: null, isDemo: false };
  const raw = String(gamePart);
  const isDemo = raw.endsWith(':demo');
  const gameId = isDemo ? raw.slice(0, -5) : raw;
  return { gameId, isDemo };
}

function casinoFromPath(pathname) {
  const parts = pathname.replace(/^\/casino\/?/, '').split('/').filter(Boolean);
  if (parts[0] === 'game' && parts[1]) {
    const { gameId, isDemo } = parseCasinoGameRoute(parts[1]);
    return { casinoTabId: '-1', casinoLobbyId: null, casinoGameId: gameId, casinoGameDemo: isDemo };
  }
  // Digitech Ana sayfa — bare /casino opens lobby home (review / -1), not All (496).
  let tabId = parts[0] || '-1';
  if (tabId === 'review') tabId = '-1';
  const lobbyId = parts[1] ? Number(parts[1]) : null;
  return { casinoTabId: tabId, casinoLobbyId: lobbyId, casinoGameId: null, casinoGameDemo: false };
}

function promosKindFromPath(pathname) {
  if (pathname.startsWith('/promos/tournaments')) return 'tournaments';
  if (pathname.startsWith('/promos/bonuses')) return 'bonuses';
  if (pathname.startsWith('/promos/promo-code')) return 'promoCode';
  return 'actions';
}

function casinoFilterFromSearch(searchParams) {
  const casinoProvider = searchParams.get('provider') || null;
  const casinoQuery = searchParams.get('q') || null;
  return { casinoProvider, casinoQuery };
}

function applyCasinoFilterParams(url, { casinoProvider, casinoQuery } = {}) {
  if (casinoProvider) url.searchParams.set('provider', casinoProvider);
  else url.searchParams.delete('provider');
  if (casinoQuery) url.searchParams.set('q', casinoQuery);
  else url.searchParams.delete('q');
}

function pathForPage(page, { helpSection, helpCategory, helpQuestion, promosKind, accountSection, casinoTabId, casinoLobbyId, casinoGameId, casinoGameDemo, cmsSlug, affiliateCode } = {}) {
  if (page === 'affiliateGate') {
    const code = String(affiliateCode || '').trim();
    return code ? `/aff/${encodeURIComponent(code)}` : '/';
  }
  if (page === 'login') return '/authProcess/login';
  if (page === 'register') return '/authProcess/registration';
  if (page === 'restorePassword') return '/authProcess/restorePassword';
  if (page === 'oauthCallback') return '/auth/oauth/callback';
  if (page === 'vip') return '/promo-vip';
  if (page === 'basindaBiz') return '/basinda-biz';
  if (page === 'cms') {
    const slug = String(cmsSlug || '').trim().replace(/^\/+|\/+$/g, '');
    return slug ? `/pages/${slug}` : '/pages/about-us';
  }
  if (page === 'account') {
    return accountPathForSection(accountSection || 'profile');
  }
  if (page === 'promos') {
    if (promosKind === 'tournaments') return '/promos/tournaments';
    if (promosKind === 'bonuses') return '/promos/bonuses';
    if (promosKind === 'promoCode') return '/promos/promo-code';
    return '/promos/actions';
  }
  if (page === 'help') {
    const locale = readAppLocale();
    const sections = getHelpSections(locale);
    const section = sections.find((s) => s.id === helpSection) || sections[0];
    if (section.id === 'faq') {
      if (helpCategory && helpQuestion) {
        return `/help-center/${section.path}/${helpCategory}/${helpQuestion}`;
      }
      if (helpCategory) {
        return `/help-center/${section.path}/${helpCategory}`;
      }
      return `/help-center/${section.path}`;
    }
    if (section.id === 'glossary') {
      if (helpQuestion) return `/help-center/${section.path}/${helpQuestion}`;
      return `/help-center/${section.path}`;
    }
    if (helpCategory && helpQuestion) {
      return `/help-center/${section.path}/${helpCategory}/${helpQuestion}`;
    }
    if (helpCategory) {
      return `/help-center/${section.path}/${helpCategory}`;
    }
    return `/help-center/${section.path}`;
  }
  if (page === 'sport') return '/sport';
  if (page === 'vurgunTv') return '/vurgun-tv';
  if (page === 'casino') {
    if (casinoGameId) {
      const suffix = casinoGameDemo ? ':demo' : '';
      return `/casino/game/${casinoGameId}${suffix}`;
    }
    const tab = casinoTabId == null || casinoTabId === '' || casinoTabId === 'review'
      ? '-1'
      : String(casinoTabId);
    // Slots Digitech home stays bare /casino; other lobbies need /casino/-1/<lobbyId>.
    if (tab === '-1' && (casinoLobbyId == null || casinoLobbyId === '')) {
      return '/casino';
    }
    let path = `/casino/${tab}`;
    if (casinoLobbyId != null && casinoLobbyId !== '') path += `/${casinoLobbyId}`;
    return path;
  }
  return '/';
}

function applySportEventParams(url, sportEvent) {
  const keys = ['type', 'sportId', 'leagueId', 'gameId', 'marketId', 'oddId', 'oddPoint', 'opId', 'oddLabel', 'oddRate'];
  keys.forEach((k) => url.searchParams.delete(k));
  if (!sportEvent) return;
  const type = sportEvent.type === 'live' ? 'live' : sportEvent.type === 'line' ? 'line' : '';
  if (type) url.searchParams.set('type', type);
  if (sportEvent.sportId) url.searchParams.set('sportId', String(sportEvent.sportId));
  if (sportEvent.leagueId) url.searchParams.set('leagueId', String(sportEvent.leagueId));
  if (sportEvent.gameId) url.searchParams.set('gameId', String(sportEvent.gameId));
  if (sportEvent.marketId) url.searchParams.set('marketId', String(sportEvent.marketId));
  if (sportEvent.oddId) url.searchParams.set('oddId', String(sportEvent.oddId));
  if (sportEvent.oddPoint) url.searchParams.set('oddPoint', String(sportEvent.oddPoint));
  if (sportEvent.opId != null && sportEvent.opId !== '') url.searchParams.set('opId', String(sportEvent.opId));
  if (sportEvent.oddLabel) url.searchParams.set('oddLabel', String(sportEvent.oddLabel));
  if (sportEvent.oddRate != null && sportEvent.oddRate !== '') url.searchParams.set('oddRate', String(sportEvent.oddRate));
}

function sportEventFromSearch(searchParams) {
  const gameId = Number(searchParams.get('gameId'));
  const sportId = Number(searchParams.get('sportId'));
  const leagueId = Number(searchParams.get('leagueId'));
  if (!gameId || !sportId || !leagueId) return null;
  const typeRaw = searchParams.get('type');
  const event = {
    type: typeRaw === 'live' ? 'live' : 'line',
    sportId,
    leagueId,
    gameId,
  };
  const marketId = Number(searchParams.get('marketId'));
  const oddId = Number(searchParams.get('oddId'));
  const oddPoint = searchParams.get('oddPoint');
  if (marketId) event.marketId = marketId;
  if (oddId) event.oddId = oddId;
  if (oddPoint) event.oddPoint = oddPoint;
  const opId = searchParams.get('opId');
  const oddLabel = searchParams.get('oddLabel');
  const oddRate = searchParams.get('oddRate');
  if (opId != null && opId !== '') event.opId = opId;
  if (oddLabel) event.oddLabel = oddLabel;
  if (oddRate) event.oddRate = oddRate;
  return event;
}

export function readRoute() {
  const promoId = new URLSearchParams(window.location.search).get('promoId');
  let pathname = stripLocalePath(window.location.pathname).pathname;

  if (pathname === '/vip' || pathname.startsWith('/vip/')) {
    replaceRoute('vip', { promoId: promoId || undefined });
    pathname = '/promo-vip';
  }

  let helpSection = 'faq';
  let helpCategory = null;
  let helpQuestion = null;
  if (pathname.startsWith('/help-center/')) {
    const parsed = parseHelpRoute(pathname, readAppLocale());
    helpSection = parsed.sectionId;
    helpCategory = parsed.categorySlug;
    helpQuestion = parsed.questionSlug;
  }

  const cmsSlug = cmsSlugFromPath(pathname);
  const accountSection = accountSectionFromPath(pathname);
  const { casinoTabId, casinoLobbyId, casinoGameId, casinoGameDemo } = casinoFromPath(pathname);
  const searchParams = new URLSearchParams(window.location.search);
  const { casinoProvider, casinoQuery } = casinoFilterFromSearch(searchParams);
  const sportEvent = pageFromPath(pathname) === 'sport' ? sportEventFromSearch(searchParams) : null;

  const affMatch = String(pathname || '').match(/\/aff\/([^/\?#]+)/);
  const affiliateCode = affMatch ? decodeURIComponent(affMatch[1]) : null;

  return {
    page: pageFromPath(pathname),
    affiliateCode,
    promoId,
    promosKind: promosKindFromPath(pathname),
    helpSection,
    helpCategory,
    helpQuestion,
    cmsSlug,
    accountSection,
    casinoTabId,
    casinoLobbyId,
    casinoGameId,
    casinoGameDemo,
    casinoProvider,
    casinoQuery,
    sportEvent,
  };
}

function notifyRouteChange() {
  window.dispatchEvent(new Event('vb-route-change'));
  resetPageScroll();
}

export function pushRoute(page, { promoId, helpSection, helpCategory, helpQuestion, promosKind, accountSection, casinoTabId, casinoLobbyId, casinoGameId, casinoGameDemo, casinoProvider, casinoQuery, cmsSlug, sportEvent } = {}) {
  const url = new URL(window.location.href);
  url.pathname = withLocalePath(pathForPage(page, { helpSection, helpCategory, helpQuestion, promosKind, accountSection, casinoTabId, casinoLobbyId, casinoGameId, casinoGameDemo, cmsSlug }));
  if (promoId) url.searchParams.set('promoId', promoId);
  else url.searchParams.delete('promoId');
  if (page === 'casino' && !casinoGameId) applyCasinoFilterParams(url, { casinoProvider, casinoQuery });
  else if (page !== 'casino') {
    url.searchParams.delete('provider');
    url.searchParams.delete('q');
  }
  if (page === 'sport') applySportEventParams(url, sportEvent || null);
  else applySportEventParams(url, null);
  window.history.pushState(
    { page, promoId: promoId || null, helpSection, helpCategory, helpQuestion, promosKind, accountSection, casinoTabId, casinoLobbyId, casinoGameId, casinoGameDemo, casinoProvider: casinoProvider || null, casinoQuery: casinoQuery || null, cmsSlug: cmsSlug || null, sportEvent: sportEvent || null },
    '',
    `${url.pathname}${url.search}`,
  );
  notifyRouteChange();
}

export function replaceRoute(page, { promoId, helpSection, helpCategory, helpQuestion, promosKind, accountSection, casinoTabId, casinoLobbyId, casinoGameId, casinoGameDemo, casinoProvider, casinoQuery, cmsSlug, sportEvent } = {}) {
  const url = new URL(window.location.href);
  url.pathname = withLocalePath(pathForPage(page, { helpSection, helpCategory, helpQuestion, promosKind, accountSection, casinoTabId, casinoLobbyId, casinoGameId, casinoGameDemo, cmsSlug }));
  if (promoId) url.searchParams.set('promoId', promoId);
  else url.searchParams.delete('promoId');
  if (page === 'casino' && !casinoGameId) applyCasinoFilterParams(url, { casinoProvider, casinoQuery });
  else if (page !== 'casino') {
    url.searchParams.delete('provider');
    url.searchParams.delete('q');
  }
  if (page === 'sport') applySportEventParams(url, sportEvent || null);
  else applySportEventParams(url, null);
  window.history.replaceState(
    { page, promoId: promoId || null, helpSection, helpCategory, helpQuestion, promosKind, accountSection, casinoTabId, casinoLobbyId, casinoGameId, casinoGameDemo, casinoProvider: casinoProvider || null, casinoQuery: casinoQuery || null, cmsSlug: cmsSlug || null, sportEvent: sportEvent || null },
    '',
    `${url.pathname}${url.search}`,
  );
  notifyRouteChange();
}

const PAGE_SCROLL_SELECTORS = [
  '.page-body',
  '.sport-page',
  '.registration-page',
  '.account-page',
  '.account-content',
  '.account-children',
  '.casino-page__main-scroll',
  '.tb--lobby-content',
  '.l5--main',
  '.main-block',
  '.lobby-home',
  '.vip-page',
  '.promos-page',
  '.help-page',
  '.mobile-account-page',
];

function scrollNodeTop(node) {
  if (!node) return;
  try {
    if (typeof node.scrollTo === 'function') node.scrollTo(0, 0);
    else node.scrollTop = 0;
  } catch {
    /* ignore */
  }
}

/** Scroll window + every known page scroller to top (Digitech casino lobby included). */
export function resetPageScroll() {
  const run = () => {
    try {
      window.scrollTo(0, 0);
    } catch {
      /* ignore */
    }
    scrollNodeTop(document.documentElement);
    scrollNodeTop(document.body);
    for (const sel of PAGE_SCROLL_SELECTORS) {
      document.querySelectorAll(sel).forEach(scrollNodeTop);
    }
  };

  run();
  window.requestAnimationFrame(() => {
    run();
    window.requestAnimationFrame(run);
  });
  window.setTimeout(run, 50);
  window.setTimeout(run, 200);
}
